import React, { useState, useRef, useEffect } from 'react';
import '../tools/css/FeedMediaCarousel.css';

/**
 * Carrusel de imágenes/video para las tarjetas del feed de publicaciones.
 * Reemplaza a react-awesome-slider: esa librería exponía sus variables CSS
 * bajo una clase interna difícil de predecir/estilizar (".awssld", no
 * ".aws-btn"), lo que causó varias rondas de ajustes. Este componente es
 * propio, minimalista y pensado para verse como una app de red social:
 * fondo desenfocado a partir de la misma imagen/video (sin franjas planas),
 * flechas, puntos, contador "1/N" y swipe táctil en móvil.
 */
const FeedMediaCarousel = ({ archivos, urlDe, esVideoDe, onClickMedia }) => {
  const [indice, setIndice] = useState(0);
  const inicioToqueX = useRef(null);
  const contenedorRef = useRef(null);

  const total = archivos.length;

  // Pausa cualquier video del carrusel (el de fondo difuminado y el de
  // primer plano con controles) en cuanto sale de la vista al hacer scroll,
  // para que no se sigan reproduciendo/sonando fuera de pantalla.
  useEffect(() => {
    const contenedor = contenedorRef.current;
    if (!contenedor) return undefined;

    const observador = new IntersectionObserver((entradas) => {
      entradas.forEach((entrada) => {
        if (!entrada.isIntersecting) {
          contenedor.querySelectorAll('video').forEach((video) => video.pause());
        }
      });
    });

    observador.observe(contenedor);
    return () => observador.disconnect();
  }, []);

  if (total === 0) return null;

  const irA = (nuevoIndice) => {
    setIndice(((nuevoIndice % total) + total) % total);
  };

  const manejarToqueInicio = (e) => {
    inicioToqueX.current = e.touches[0].clientX;
  };

  const manejarToqueFin = (e) => {
    if (inicioToqueX.current === null) return;
    const delta = e.changedTouches[0].clientX - inicioToqueX.current;
    if (Math.abs(delta) > 40) {
      irA(indice + (delta < 0 ? 1 : -1));
    }
    inicioToqueX.current = null;
  };

  return (
    <div className="fmc" ref={contenedorRef} onTouchStart={manejarToqueInicio} onTouchEnd={manejarToqueFin}>

      <div className="fmc__track" style={{ transform: `translateX(-${indice * 100}%)` }}>
        {archivos.map((archivo, i) => {
          const url = urlDe(archivo);
          const esVideo = esVideoDe(archivo);
          const activa = i === indice;

          return (
            <div className="fmc__slide" key={i}>

              {esVideo ? (
                activa
                  ? <video src={url} className="fmc__backdrop" muted autoPlay loop playsInline aria-hidden="true" />
                  : <div className="fmc__backdrop fmc__backdrop--placeholder" aria-hidden="true" />
              ) : (
                <img src={url} className="fmc__backdrop" alt="" aria-hidden="true" loading="lazy" />
              )}

              {esVideo ? (
                <video src={url} className="fmc__media" controls preload="metadata" />
              ) : (
                <img
                  src={url}
                  className={"fmc__media" + (onClickMedia ? " fmc__media--clickable" : "")}
                  alt="Publicación"
                  onClick={onClickMedia ? () => onClickMedia(archivo) : undefined}
                />
              )}

            </div>
          );
        })}
      </div>

      {total > 1 && (
        <React.Fragment>

          <button type="button" className="fmc__arrow fmc__arrow--prev" onClick={() => irA(indice - 1)} aria-label="Anterior">
            <i className="fas fa-chevron-left"></i>
          </button>
          <button type="button" className="fmc__arrow fmc__arrow--next" onClick={() => irA(indice + 1)} aria-label="Siguiente">
            <i className="fas fa-chevron-right"></i>
          </button>

          <div className="fmc__contador">{indice + 1}/{total}</div>

          <div className="fmc__dots">
            {archivos.map((_, i) => (
              <button
                type="button"
                key={i}
                className={"fmc__dot" + (i === indice ? " fmc__dot--activo" : "")}
                onClick={() => irA(i)}
                aria-label={"Ir a la imagen " + (i + 1)}
              />
            ))}
          </div>

        </React.Fragment>
      )}

    </div>
  );
};

export default FeedMediaCarousel;
